import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { Divider, useTheme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBack from '@mui/icons-material/ArrowBack';
import {
  BoxSection,
  ButtonDefault,
  ButtonPrimary,
  ConfirmModal,
  Heading3,
  Spinner,
  TextL,
  TextM,
  TextS,
} from 'app/components';
import { formatCNPJ, formatCPF, formatPhone } from 'app/helpers';
import AttentionCircularIcon from 'app/components/Icons/AttentionCircularIcon';
import {
  TRegisterBusinessDataRequest,
  TRegisterPersonDataRequest,
} from 'modules/customer/context';
import {
  PersonFileHttpService,
  useCustomerService,
  usePersonService,
} from 'modules/customer/services';
import { ListBox } from '../components';
import {
  BorrowerStepContent,
  InfoArea,
  ListBoxItem,
  ListBoxSection,
  QualificationModalContent,
  StyledTitle,
} from './styles';
import { ApplicationRequestContext } from '../ApplicationRequest';
import { StepContentButtonsArea } from '../styles';
import { useQualificationsService } from 'modules/products/services/hooks';
import { TQualification } from 'modules/customer/interfaces/qualifications';
import CreatePersonModal from 'modules/common/CreatePersonModal/CreatePersonModal';
import {
  EPersonType,
  usePersonContext,
} from 'modules/customer/context/PersonProvider/PersonProvider';
import {
  EDocumentType,
  EMaritalPropertySystem,
  EMaritalStatus,
  EPersonFunctions,
  ESex,
} from 'modules/customer/context/PersonProvider/person.interfaces';
import CreatePersonForm from 'modules/common/CreatePersonModal/About/CreatePersonForm/CreatePersonForm';
import IdentificationStep from 'modules/common/CreatePersonModal/Identification/Identification';
import AddressStep from 'modules/common/CreatePersonModal/Address/Address';
import BankStep from 'modules/common/CreatePersonModal/Bank/Bank';
import {
  serializeOnlyNumbers,
  serializePhoneObject,
} from 'app/utils/normalizer';
import UploadDocument from 'app/components/UploadNewVersion/Upload';
import {
  EPersonDocument,
  naturalPersonDocumentsList,
} from 'app/components/UploadNewVersion/constants';
import { IDocument } from 'app/components/UploadNewVersion/interfaces';
import { NaturalPersonViewDocumentsSection } from 'modules/customer/pages/NaturalPersonViewPage/components/NaturalPersonViewDocumentsSection';
import { TypeProductEmission } from 'modules/products/context/ProductsProvider/products.interfaces';
import {
  EIncorporationType,
  IBankAccount,
} from 'modules/customer/context/CustomerProvider/customer.interfaces';
import {
  convertBorrowerIntoCompany,
  convertBusinessPersonIntoPerson,
} from 'app/utils/converters';
import { useSnackbar } from 'app/hooks/useSnackbar';
import { EEducationLevel } from 'modules/common/CreatePersonModal/types';

export type ItemPersonType = TRegisterPersonDataRequest;
export type ItemBusinessType = TRegisterBusinessDataRequest;
export type TBorrowerType = 'PERSON' | 'BUSINESS';

type Address = {
  country_code: string;
  street_name: string;
  street_number: string;
  district: string;
  postal_code: string;
  city: string;
  state_code: string;
  extra_info: string;
};

const BorrowerInfoDivider = () => {
  return (
    <Divider
      orientation="vertical"
      sx={{
        backgroundColor: '#E6E5EA',
        height: '21px',
        width: '2px',
        margin: '0px 24px',
      }}
    />
  );
};

const filterItemsPerson = [
  { name: 'Nome', parameterName: 'full_name' },
  { name: 'CPF', parameterName: 'taxpayer_id' },
  { name: 'Email', parameterName: 'email_address' },
];

const filterItemsBusiness = [
  { name: 'Nome', parameterName: 'legal_name' },
  { name: 'CNPJ', parameterName: 'taxpayer_id' },
  { name: 'Email', parameterName: 'email_address' },
];

type TBorrowerStepProps = {
  isUnicPerson?: boolean;
};

const BorrowerStep = ({ isUnicPerson }: TBorrowerStepProps) => {
  const theme = useTheme();
  const { showSnackbar } = useSnackbar();
  const { getPersonList, personListLoading } = usePersonService();
  const {
    getBusinessList,
    creatBusinessLoading,
    getBusinessRelations,
    businessRelationsLoading,
  } = useCustomerService();
  const {
    product,
    borrowerData,
    borrowerQualification,
    updateBorrowerQualification,
    updateBorrowerData,
    onBack,
    onForward,
  } = useContext(ApplicationRequestContext);

  const {
    setPerson,
    setPersonCompany,
    setRepresentatives,
    person,
    updatePerson,
    createPerson,
    isLoading: personLoading,
    documents: uploadedFiles,
    setDocuments: setUploadedFiles,
  } = usePersonContext();

  const currentPage = useRef<number>(0);
  const searchParameter = useRef<string>('');
  const searchValue = useRef<string>('');

  const [isLoading, setIsLoading] = useState(false);
  const [missingFieldsMessage, setMissingFieldsMessage] = useState<string>();
  const [isOpenEditPersonModal, setIsOpenEditPersonModal] = useState(false);
  const [isOpenCreateModal, setIsOpenCreateModal] = useState(false);
  const [isOpenInvalidBorrowerModal, setIsOpenInvalidBorrowerModal] =
    useState(false);
  const [isOpenMissingInfoBorrowerModal, setIsOpenMissingInfoBorrowerModal] =
    useState(false);
  const [isQualificationAlertModalOpen, toggleQualificationModalAlertOpen] =
    useState<boolean>(false);
  const [hasMoreItems, updateHasMoreItems] = useState<boolean>(false);
  const { getBorrowerQualification, getBorrowerLimits } =
    useQualificationsService();
  const [personBorrowerList, setPersonBorrowerList] = useState<
    TRegisterPersonDataRequest[]
  >([]);
  const [businessBorrowerList, setBusinessBorrowerList] = useState<
    TRegisterBusinessDataRequest[]
  >([]);

  const isPerson = (product?.borrower_type as TBorrowerType) === 'PERSON';

  useEffect(() => {
    updateBorrowerQualification(null);
    if (borrowerData) {
      if (isPerson) {
        setPersonBorrowerList([borrowerData]);
        return;
      }
      setBusinessBorrowerList([borrowerData]);
      return;
    }
  }, []);

  const onForwardRequest = useCallback(async () => {
    let personId = person.id;

    if (isUnicPerson && person?.taxpayer_id) {
      // Validate fields
      if (
        !person?.full_name ||
        !person?.nationality ||
        !person?.occupation ||
        !person?.phone ||
        !person.email_address ||
        !person?.birth_date ||
        !person?.mothers_name
      ) {
        return showSnackbar('Preencha todos os campos obrigatórios.', 'error');
      }

      if (
        !person?.identification?.document_issuer ||
        !person?.identification?.document_number ||
        !person?.identification?.document_type?.value
      ) {
        return showSnackbar(
          'Preencha todos os campos de Identificação.',
          'error',
        );
      }

      if (
        !person?.address?.postal_code ||
        (!person?.address?.city?.value && !person?.address?.cityManually) ||
        !person?.address?.state_code?.value
      ) {
        return showSnackbar('Preencha todos os campos de Endereço.', 'error');
      }

      const parsedPayload = {
        full_name: person?.full_name || '',
        nationality: person?.nationality || '',
        occupation: person?.occupation || '',
        phone: serializePhoneObject(person?.phone),
        address: {
          city: (person?.address?.city?.value as string) || 'não informado',
          country_code: 'BRA',
          district: person?.address?.district || 'não informado',
          extra_info: person?.address?.extra_info || '',
          postal_code: serializeOnlyNumbers(person?.address?.postal_code) || '',
          state_code: person?.address?.state_code?.value || '',
          street_name: person?.address?.street_name || 'não informado',
          street_number: person?.address?.street_number || '0',
        },
        birth_date: dayjs(person?.birth_date).format('YYYY-MM-DD'),
        birthplace: person?.birthplace,
        email_address: person?.email_address,
        external_bank_account: person?.external_bank_account,
        education_level:
          person?.education_level || EEducationLevel.NOT_INFORMED,
        id_document: {
          issue_date: person?.identification?.document_issue_date
            ? dayjs(person?.identification?.document_issue_date).format(
                'YYYY-MM-DD',
              )
            : null,
          issuer: person?.identification?.document_issuer || '',
          number: person?.identification?.document_number || '',
          type: person?.identification?.document_type?.value as EDocumentType,
        },
        marital_property_system: person?.marital_property_system
          ?.value as EMaritalPropertySystem,
        marital_status:
          (person?.marital_status?.value as EMaritalStatus) || null,
        mothers_name: person?.mothers_name,
        monthly_income: parseFloat(person?.monthly_income || '0'),
        pep: !!(person?.pep?.value === 'true'),
        pix: person?.pix,
        sex: (person?.sex?.value as ESex) || null,
        spouse_date_of_birth: person?.spouse_date_of_birth
          ? dayjs(person?.spouse_date_of_birth).format('YYYY-MM-DD')
          : undefined,
        spouse_full_name: person?.spouse_full_name,
        spouse_taxpayer_id: person?.spouse_taxpayer_id?.replace(/[^\d]/g, ''),
        taxpayer_id: serializeOnlyNumbers(person?.taxpayer_id),
        role: EPersonFunctions.BORROWER,
      };

      const personFilesHttpService = new PersonFileHttpService();

      if (personId) {
        /* Edit Person Borrower **/
        await updatePerson(personId, parsedPayload);
      } else {
        /* Create Person Borrower **/
        const personResponse = await createPerson(parsedPayload);

        if (!personResponse) {
          return showSnackbar(
            'Ocorreu um erro durante a criação da pessoa.',
            'error',
          );
        }

        if (personResponse?.id) {
          personId = personResponse.id;
          setPerson({ ...person, id: personResponse.id });

          uploadedFiles?.forEach(async (doc: IDocument) => {
            if (doc.file) {
              const formData = new FormData();
              formData.append('file', doc.file);
              formData.append('type', doc.key as EPersonDocument);

              await personFilesHttpService.uploadFilesToPerson(
                formData,
                personResponse.id,
              );
            }
          });
        }
      }
    }

    if (product && (borrowerData || person)) {
      const parsedPerson = {
        ...person,
        id: personId || person?.id,
        taxpayer_id: person?.taxpayer_id || '',
        full_name: person?.full_name || '',
        nationality: person?.nationality || '',
        pep: person?.pep?.value === 'true',
        phone: serializePhoneObject(person?.phone),
        occupation: person?.occupation || '',
        marital_property_system: person?.marital_property_system
          ?.value as EMaritalPropertySystem,
        marital_status:
          (person?.marital_status?.value as EMaritalStatus) || null,
        sex: person?.sex?.value as ESex,
        education_level:
          person?.education_level || EEducationLevel.NOT_INFORMED,
        address: {
          city: (person?.address?.city?.value as string) || '',
          country_code: 'BRA',
          district: person?.address?.district || '',
          extra_info: person?.address?.extra_info || '',
          postal_code: serializeOnlyNumbers(person?.address?.postal_code) || '',
          state_code: person?.address?.state_code?.value || '',
          street_name: person?.address?.street_name || '',
          street_number: person?.address?.street_number || '',
        },
        role: undefined,
        monthly_income: parseFloat(person?.monthly_income || '0'),
        legal_name: person?.full_name || '',
        foundation_date: '',
        tax_regime: '',
        email_address: person?.email_address || '',
        pix: person?.pix,
        external_bank_account: person?.external_bank_account,
      };

      if (personId) {
        updateBorrowerData(parsedPerson);
      }

      const qualificationData = await getBorrowerQualification(
        product.id,
        personId ? personId : (borrowerData?.id as string),
      );
      const qualification = qualificationData?.content?.[0];
      if (product.conditions_required && !qualification) {
        toggleQualificationModalAlertOpen(true);
        return;
      }
      if (qualification) {
        const limits = await getBorrowerLimits(
          product.id,
          personId ? personId : (borrowerData?.id as string),
        );
        const condition = limits?.content?.[0]?.condition;
        if (
          product.conditions_required &&
          (qualification.status !== 'QUALIFIED' || !condition)
        ) {
          toggleQualificationModalAlertOpen(true);
          return;
        }
        if (limits?.content?.[0]) {
          updateBorrowerQualification(limits?.content?.[0] as TQualification);
        }
      }

      // Validações de Nota Comercial
      const isCommercialPapel =
        product.type_product_emission === TypeProductEmission.COMMERCIAL_PAPER;

      if (isCommercialPapel) {
        const isValidIncorporationType =
          borrowerData?.incorporation_type &&
          [EIncorporationType.LTDA, EIncorporationType.SA].includes(
            borrowerData?.incorporation_type,
          );

        const validateAddressFields = (address: Address): string[] => {
          const requiredFields: { key: keyof Address; label: string }[] = [
            { key: 'postal_code', label: 'Código postal' },
            { key: 'city', label: 'Cidade' },
            { key: 'state_code', label: 'Estado' },
          ];

          const emptyFields = requiredFields
            .filter(({ key }) => address[key] === '')
            .map(({ label }) => label);

          return emptyFields;
        };

        const validateBankAccountFields = (account: IBankAccount): string[] => {
          const requiredFields: {
            key: keyof IBankAccount;
            label: string;
          }[] = [
            { key: 'bank_code', label: 'Número do banco' },
            { key: 'bank_account', label: 'Número da conta' },
            { key: 'bank_account_digit', label: 'Dígito da conta' },
            { key: 'bank_account_type', label: 'Tipo da conta' },
            { key: 'bank_branch', label: 'Agência' },
            { key: 'ispb_code', label: 'Banco' },
          ];

          const emptyFields = requiredFields
            .filter(({ key }) => account[key] === '')
            .map(({ label }) => label);

          return emptyFields;
        };

        if (!isValidIncorporationType || !borrowerData) {
          return setIsOpenInvalidBorrowerModal(true);
        }

        const relationsResponse =
          (await getBusinessRelations(borrowerData?.id || '')) || [];

        const representatives =
          relationsResponse?.filter((item) => item.type === 'PARTNER') || [];
        const signers =
          representatives?.filter((item) => (item.signer ? true : false)) || [];

        setPersonCompany(
          convertBorrowerIntoCompany(borrowerData, representatives),
        );
        setRepresentatives(
          representatives.map((item) => convertBusinessPersonIntoPerson(item)),
        );

        if (!representatives || (representatives && signers.length <= 0)) {
          setMissingFieldsMessage(
            'Para criar a operação, complete o cadastro do tomador e tente novamente. Campos obrigatórios: Sócios que assinem pela empresa',
          );
          return setIsOpenMissingInfoBorrowerModal(true);
        }

        if (!borrowerData?.external_bank_account) {
          setMissingFieldsMessage(
            'Para criar a operação, complete o cadastro do tomador e tente novamente. Campos obrigatórios: Dados bancários',
          );
          return setIsOpenMissingInfoBorrowerModal(true);
        }

        const missingFields = validateAddressFields(borrowerData?.address);
        const missingBankFields = validateBankAccountFields(
          borrowerData.external_bank_account,
        );

        if (missingFields.length > 0) {
          setMissingFieldsMessage(`Para criar a operação, complete o cadastro do tomador e tente
          novamente. Campos obrigatórios: ${missingFields.join(', ')}`);
          return setIsOpenMissingInfoBorrowerModal(true);
        }

        if (missingBankFields.length > 0) {
          setMissingFieldsMessage(`Para criar a operação, complete o cadastro do tomador e tente
          novamente. Campos obrigatórios: ${missingBankFields.join(', ')}`);
          return setIsOpenMissingInfoBorrowerModal(true);
        }
      }

      onForward();
    }
  }, [borrowerData, product, onForward, isUnicPerson, person]);

  //called by debounce on first render
  const onSearch = (field: string, value: string) => {
    searchParameter.current = field;
    searchValue.current = value;
    currentPage.current = 0;
    setPersonBorrowerList([]);
    setBusinessBorrowerList([]);

    if (isPerson) {
      if (field === '') {
        handleGetPerson(0);
        return;
      }

      handleGetPerson(0, field, value);
      return;
    }

    if (field === '') {
      handleGetBusiness(0);
      return;
    }
    handleGetBusiness(0, field, value);
    updateBorrowerData(null);
  };

  const handleGetPerson = useCallback(
    async (
      page: number,
      searchParameterName?: string,
      searchParameterValue?: string,
    ) => {
      setIsLoading(true);
      const res = await getPersonList(
        page,
        searchParameterName,
        searchParameterValue,
        undefined,
        true,
      );
      updateHasMoreItems(!!res?.has_next);
      setPersonBorrowerList((state) =>
        state.concat(
          res?.content.filter((item) => {
            const index = state.findIndex((el) => el.id === item.id);
            return index < 0;
          }) || [],
        ),
      );
      currentPage.current = page;
      setIsLoading(false);
    },
    [currentPage],
  );

  const handleGetBusiness = useCallback(
    async (
      page: number,
      searchParameterName?: string,
      searchParameterValue?: string,
    ) => {
      setIsLoading(true);
      const res = await getBusinessList(
        page,
        searchParameterName,
        searchParameterValue,
        undefined,
        true,
      );
      updateHasMoreItems(!!res?.has_next);
      setBusinessBorrowerList((state) =>
        state.concat(
          res?.content.filter((item) => {
            const index = state.findIndex((el) => el.id === item.id);
            return index < 0;
          }) || [],
        ),
      );
      currentPage.current = page;
      setIsLoading(false);
    },
    [currentPage],
  );

  return (
    <BoxSection>
      <BorrowerStepContent>
        <Heading3>
          {isUnicPerson && person?.id ? (
            'Verifique o cadastro do tomador'
          ) : isUnicPerson && !person?.id ? (
            'Complete o cadastro do tomador'
          ) : (
            <>
              Para qual <strong>tomador </strong>é a solicitação?
            </>
          )}
        </Heading3>
        {isUnicPerson ? (
          <div style={{ marginRight: '1rem' }}>
            <StyledTitle>Sobre</StyledTitle>
            <CreatePersonForm
              setPerson={(createdPerson) =>
                setPerson({ ...person, ...createdPerson })
              }
              person={person}
              updateExistsPerson={() => {}}
              hasTaxId={person?.taxpayer_id}
            />
            <Divider />
            <StyledTitle>Identificação</StyledTitle>
            <IdentificationStep
              identification={person?.identification}
              hideTitle
              setIdentification={(identification) =>
                setPerson({ ...person, identification })
              }
            />
            <Divider />
            <StyledTitle>Endereço</StyledTitle>
            <AddressStep
              hideTitle
              address={person?.address}
              setAddress={(address) => setPerson({ ...person, address })}
            />
            <Divider />
            <BankStep
              hideSection
              setBankInfo={(bankInfo) =>
                setPerson({
                  ...person,
                  external_bank_account: bankInfo?.external_bank_account,
                  pix: bankInfo?.pix,
                })
              }
              bankInfo={{
                external_bank_account: person?.external_bank_account,
                pix: person?.pix,
              }}
            />
            <Divider />
            <StyledTitle>Documentos</StyledTitle>
            {person?.id ? (
              <NaturalPersonViewDocumentsSection
                personId={person.id}
                hideTitle
              />
            ) : (
              <UploadDocument
                documentsList={naturalPersonDocumentsList}
                setUploadedFiles={setUploadedFiles}
                uploadedFiles={uploadedFiles}
                hideTitle
              />
            )}
          </div>
        ) : (
          <>
            <ListBox
              hasMoreItems={hasMoreItems}
              filterItems={isPerson ? filterItemsPerson : filterItemsBusiness}
              items={
                personBorrowerList.length > 0
                  ? personBorrowerList
                  : businessBorrowerList
              }
              onSearch={onSearch}
              isLoading={isLoading || personListLoading || creatBusinessLoading}
              handleRequest={
                isPerson
                  ? () =>
                      handleGetPerson(
                        currentPage.current + 1,
                        searchParameter.current
                          ? searchParameter.current
                          : undefined,
                        searchValue.current ? searchValue.current : undefined,
                      )
                  : () =>
                      handleGetBusiness(
                        currentPage.current + 1,
                        searchParameter.current
                          ? searchParameter.current
                          : undefined,
                        searchValue.current ? searchValue.current : undefined,
                      )
              }
            >
              {(i) => {
                const item = i as ItemPersonType & ItemBusinessType;
                return (
                  <ListBoxItem
                    className={borrowerData?.id === item.id ? 'selected' : ''}
                    onClick={() => updateBorrowerData(item)}
                    key={item.taxpayer_id}
                  >
                    <TextL
                      style={{
                        color:
                          borrowerData?.id === item.taxpayer_id
                            ? theme.palette.brand.primary.base
                            : theme.palette.brand.secondary.base,
                      }}
                      weight="semibold"
                    >
                      {isPerson
                        ? item?.full_name || ''
                        : item?.legal_name || ''}
                    </TextL>
                    <InfoArea>
                      <TextS>
                        {isPerson
                          ? formatCPF(item?.taxpayer_id || '')
                          : formatCNPJ(item?.taxpayer_id || '')}
                      </TextS>
                      <BorrowerInfoDivider />
                      <TextS>
                        {formatPhone(
                          item.phone.area_code + item.phone.number || '',
                        )}
                      </TextS>
                      <BorrowerInfoDivider />
                      <TextS>{item?.email_address || ''}</TextS>
                    </InfoArea>
                  </ListBoxItem>
                );
              }}
            </ListBox>
            <ListBoxSection>
              <TextM weight="Semibold">Não cadastrou o tomador?</TextM>
              <ButtonPrimary
                style={{ maxWidth: '30%' }}
                onClick={() => setIsOpenCreateModal(true)}
              >
                <AddIcon />
                <TextM
                  weight="Semibold"
                  style={{ color: theme.palette.common.white }}
                >
                  Cadastrar {isPerson ? 'pessoa' : 'empresa'}
                </TextM>
              </ButtonPrimary>
            </ListBoxSection>
          </>
        )}
      </BorrowerStepContent>
      <StepContentButtonsArea>
        <ButtonDefault margin={true} onClick={() => onBack()}>
          <ArrowBack />
        </ButtonDefault>
        <ButtonPrimary
          disabled={
            (isUnicPerson && !person) || (!isUnicPerson && !borrowerData)
          }
          onClick={onForwardRequest}
        >
          {businessRelationsLoading ? (
            <Spinner color={theme.palette.common.white} size={18} />
          ) : (
            'Avançar'
          )}
        </ButtonPrimary>
      </StepContentButtonsArea>

      {/** Modals */}
      <ConfirmModal
        isColumnButtons
        isOpen={isOpenInvalidBorrowerModal}
        icon={<AttentionCircularIcon />}
        title="Tomador inválido"
        btnConfirmText="Ok"
        handleConfirm={() => setIsOpenInvalidBorrowerModal(false)}
        handleClose={() => setIsOpenInvalidBorrowerModal(false)}
      >
        <p>
          Para este tipo de solicitação, a empresa precisa se adequar ao modelo
          SA ou LTDA.
        </p>
      </ConfirmModal>

      <ConfirmModal
        isOpen={isOpenMissingInfoBorrowerModal}
        icon={<AttentionCircularIcon />}
        title="Tomador com cadastro incompleto"
        btnConfirmText="Completar cadastro"
        btnCancelText="Cancelar"
        handleCancel={() => setIsOpenMissingInfoBorrowerModal(false)}
        handleConfirm={() => {
          setIsOpenEditPersonModal(true);
          setIsOpenMissingInfoBorrowerModal(false);
        }}
        handleClose={() => setIsOpenMissingInfoBorrowerModal(false)}
      >
        <p>{missingFieldsMessage}</p>
      </ConfirmModal>

      <ConfirmModal
        isColumnButtons={true}
        isOpen={isQualificationAlertModalOpen}
        icon={<AttentionCircularIcon />}
        title={
          borrowerQualification?.status === 'QUALIFIED' &&
          product?.conditions_required &&
          !borrowerQualification?.condition
            ? `${
                isPerson ? borrowerData?.full_name : borrowerData?.legal_name
              } não possui condições configuradas.`
            : `${
                isPerson ? borrowerData?.full_name : borrowerData?.legal_name
              } ainda não foi aprovado para este produto.`
        }
        btnConfirmText="Limpar e informar outro tomador"
        handleSkip={!product?.qualification_required ? onForward : undefined}
        btnSkipText="Continuar mesmo assim"
        handleConfirm={() => {
          toggleQualificationModalAlertOpen(false);
          updateBorrowerData(null);
          updateBorrowerQualification(null);
        }}
        handleClose={() => toggleQualificationModalAlertOpen(false)}
        handleCancel={() => toggleQualificationModalAlertOpen(false)}
      >
        <QualificationModalContent>
          <TextM>
            {`O tomador selecionado não possui linha de crédito para o produto `}
            <strong>{product?.name}</strong>
          </TextM>
          <a
            href={
              isPerson
                ? `#/records/natural/${borrowerData?.id}/qualifications`
                : `#/records/legal/${borrowerData?.id}/qualifications`
            }
            target="_blank"
            style={{
              textDecoration: 'underline',
              marginTop: '20px',
              textAlign: 'right',
            }}
            className="link"
            rel="noreferrer"
          >
            Ir para o cadastro
          </a>
        </QualificationModalContent>
      </ConfirmModal>

      {(isLoading || personLoading) && <Spinner fullscreen />}

      {isOpenCreateModal && (
        <CreatePersonModal
          handleClose={() => setIsOpenCreateModal(false)}
          isOpen={isOpenCreateModal}
          title="Criar Tomador"
          formTypeRender={isPerson ? EPersonType.PERSON : EPersonType.COMPANY}
          hasBankInformation
          successMessage="Tomador cadastrado com sucesso!"
        />
      )}

      {isOpenEditPersonModal && (
        <CreatePersonModal
          handleClose={() => {
            setIsOpenEditPersonModal(false);
            onSearch('legal_name', borrowerData?.legal_name || '');
          }}
          isOpen={isOpenEditPersonModal}
          title="Editar Tomador"
          formTypeRender={EPersonType.COMPANY}
          hasBankInformation
          successMessage="Tomador editado com sucesso!"
        />
      )}
    </BoxSection>
  );
};

export default BorrowerStep;
